import { useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import styled from "styled-components/macro";
import { useAuthContext } from "../../../../../components/Auth";
import { PageTitle, PageWrapper } from "../../../../../layout/portalPageLayout";
import {
  ApprovalsListTable,
  buildDefaultFilters,
} from "../../../../SharedPages/ProductDetailPage/Approvals/ApprovalsList";
import { useStorefrontApprovals } from "../../../../SharedPages/ProductDetailPage/Approvals/hooks/useStorefrontApprovals";
import { useApprovalsFilters } from "../../../../SharedPages/ProductDetailPage/Approvals/hooks/useApprovalsFilters";
import { useApprovalsListControls } from "../../../../SharedPages/ProductDetailPage/Approvals/hooks/useApprovalsListControls";
import { useSeedDefaultApproverFilter } from "../../../../SharedPages/ProductDetailPage/Approvals/hooks/useSeedDefaultApproverFilter";
import type { ApprovalWorkflow } from "../../../../../types/types.Workflow";
import { useStoreState } from "../../../../../util/util";
import { ApprovalsSummaryTiles } from "./ApprovalsSummaryTiles";

const PageHeader = styled.div`
  margin-bottom: ${({ theme }) => theme.resolve("spacing_3xl")};
`;

export function ApprovalDashboard(): JSX.Element {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const { user } = useAuthContext();
  const { storefront_id, storefront_metadata } = useStoreState();

  const {
    perPage,
    offset,
    searchQuery,
    filters,
    listFilterParams,
    onPageChange,
    onPerPageChange,
    onSearchChange,
    onClearSearch,
    onFiltersChange,
    setFilters,
  } = useApprovalsListControls(
    buildDefaultFilters(user),
    `approvals_list:${storefront_id}:dashboard`
  );

  const {
    approvals,
    pagination,
    isLoading,
    isFetching,
    error,
    total_approvals_count,
    summary_tiles,
    list_details,
    mutate,
  } = useStorefrontApprovals({
    limit: perPage,
    offset,
    ...listFilterParams,
  });

  const { filters: apiFilters } = useApprovalsFilters(listFilterParams);

  useSeedDefaultApproverFilter({
    user,
    apiFilters,
    setFilters,
  });

  const handleRowClick = (
    approval: ApprovalWorkflow,
    e: React.MouseEvent<HTMLTableRowElement>
  ) => {
    const tenantSlug = storefront_metadata?.slug;
    const productKey = approval.product_number;
    if (tenantSlug && productKey) {
      const url = `/store/${tenantSlug}/admin/pim/products/${productKey}/approvals/${approval.id}${location.search}`;
      if (e.metaKey || e.ctrlKey) {
        window.open(url, "_blank", "noopener,noreferrer");
      } else {
        history.push(url);
      }
    }
  };

  return (
    <PageWrapper>
      <PageHeader>
        <PageTitle>{t("Approvals")}</PageTitle>
      </PageHeader>
      <ApprovalsSummaryTiles
        tiles={summary_tiles}
        isLoading={isLoading && summary_tiles.length === 0}
      />
      <ApprovalsListTable
        variant="dashboard"
        approvals={approvals}
        pagination={pagination}
        isLoading={isFetching}
        error={error}
        onRowClick={handleRowClick}
        onPageChange={onPageChange}
        onPerPageChange={onPerPageChange}
        perPage={perPage}
        offset={offset}
        searchQuery={searchQuery}
        onSearchChange={onSearchChange}
        onClearSearch={onClearSearch}
        filters={filters}
        onFiltersChange={onFiltersChange}
        apiFilters={apiFilters}
        listDetails={list_details}
        totalApprovalsCount={total_approvals_count}
        onAssigneeUpdated={() => {
          void mutate();
        }}
      />
    </PageWrapper>
  );
}
